<template>
  <div class="cashback__wrapper">
    <div v-if="!hasError" class="cashback__wrapper">
      <NavBar>
        <template #left>
          <BackButton @click="onBack" />
        </template>
        <template #right>
          <ShowMoneyToggleButton />
        </template>
      </NavBar>
      <div class="cashback__header">
        <div class="cashback__header--container">
          <SkeletonLoader v-if="isLoading && !cashbackDetail?.card">
            <div class="cashback__header-loader" />
          </SkeletonLoader>
          <div
            v-if="!isLoading || cashbackDetail?.card"
            class="cashback__header--title"
          >
            <BaseText size="p4">{{ cashbackDetail?.card.title }}</BaseText>
            <Heading :level="2" class="cashback__header--title">
              <Money>
                {{ cashbackDetail?.card.value }}
              </Money>
            </Heading>
          </div>
          <div
            v-if="!isLoading || cashbackDetail?.card"
            class="cashback__header--disclaimer"
          >
            <BaseText size="p4">
              {{ cashbackDetail?.card.description }}
            </BaseText>
          </div>
        </div>
      </div>
      <main class="cashback">
        <section class="cashback__table">
          <div
            v-if="availableYears && !isLoading && cashbackDetail?.card.summary"
            class="cashback__table--heading-container"
          >
            <div class="cashback__table--heading">
              <Heading :level="3">Histórico</Heading>
            </div>
            <FilterSelect
              :options="yearsOptions"
              label=""
              render-menu-with-portal
              use-option-label
              :value="selectedYear"
              @change="onChange"
            />
          </div>
          <div>
            <div v-if="!isLoading" class="cashback__rows">
              <Row
                v-for="(month, index) in cashbackDetail?.card.summary"
                :key="index"
                :class="['cashback__rows--row', { disabled: month.disabled }]"
                :chevron="!month.disabled"
                :has-interaction="!month.isDisabled"
                :has-line="false"
                :left-title="month.title"
                @click="onRowClick(month)"
              >
                <template #top-right>
                  <Money>
                    {{ month.value }}
                  </Money>
                </template>
              </Row>
            </div>
            <div class="cashback__loader-wrapper">
              <SkeletonLoader v-if="isLoading">
                <div class="cashback__loader" />
                <div class="cashback__loader" />
                <div class="cashback__loader" />
                <div class="cashback__loader" />
                <div class="cashback__loader" />
                <div class="cashback__loader" />
                <div class="cashback__loader" />
                <div class="cashback__loader" />
                <div class="cashback__loader" />
                <div class="cashback__loader" />
                <div class="cashback__loader" />
                <div class="cashback__loader" />
              </SkeletonLoader>
            </div>
          </div>
        </section>
        <CashbackSideSheet
          :is-open="isSideSheetOpen"
          :year="selectedYear"
          :month="selectedMonth"
          @close="isSideSheetOpen = false"
        />
      </main>
    </div>
    <div v-else class="cashback__wrapper--error">
      <HelperViewStatus
        generic-error
        button-label="Tentar novamente"
        @click="fetchCashbackDetails(selectedYear, true)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';

import {
  NavBar,
  Heading,
  BaseText,
  FilterSelect,
  Row,
  SkeletonLoader,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { ShowMoneyToggleButton } from '@/modules/common/components/buttons/ShowMoneyToggleButton';

import {
  getCashbackDetails,
  ICashbackDetail,
  ISummaryV2
} from '@/modules/cash/services/costs';
import CashbackSideSheet from '@/modules/cash/views/Cashback/components/CashbackSideSheet/CashbackSideSheet.vue';
import { Money } from '@/modules/common/components/Money';

const isLoading = ref(false);
const hasError = ref(false);
const selectedYear = ref('');
const selectedMonth = ref<ISummaryV2>({} as ISummaryV2);
const isSideSheetOpen = ref(false);
const availableYears = ref<string[] | null>(null);
const cashbackDetail = ref<ICashbackDetail | null>(null);
const router = useRouter();

const yearsOptions = computed(() => {
  return availableYears.value?.map(year => {
    return {
      label: String(year),
      value: String(year)
    };
  });
});

function onRowClick(month: ISummaryV2) {
  if (month.isDisabled) return;
  selectedMonth.value = month;
  isSideSheetOpen.value = true;
}

function onBack() {
  router.back();
}

async function onChange(value: string) {
  selectedYear.value = value;
  await fetchCashbackDetails(selectedYear.value);
}

async function fetchCashbackDetails(year: string, setSelectedYear = false) {
  hasError.value = false;
  try {
    isLoading.value = true;

    const data = await getCashbackDetails(year);

    availableYears.value = data.availableYears;
    cashbackDetail.value = data;

    if (setSelectedYear && !selectedYear.value && data.availableYears.length) {
      selectedYear.value = availableYears.value[0];
    }
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

onMounted(async () => {
  await fetchCashbackDetails(selectedYear.value, true);
});
</script>

<style lang="less" scoped>
.wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
}

.skeleton-container {
  width: 100%;
}

.cashback__header-loader {
  width: 100%;
  height: 78px;
}

.cashback__loader {
  width: 100%;
  height: 26px;
}

.cashback__wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &--error {
    height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
}

.cashback__loader-wrapper {
  width: 100%;
  margin-top: @size-spacing-x700;
}

.navbar-container {
  border-bottom: none;
  background-color: @background-primary;
}

.cashback {
  width: 100%;
  max-width: 1008px;
  margin: 0 auto;
  padding: 0 @size-spacing-x450;

  &__rows {
    display: flex;
    gap: @size-spacing-x600;
    flex-wrap: wrap;
    border-top: @size-border-x400 solid @divider-primary;
    margin-top: @size-spacing-x700;
    row-gap: @size-spacing-x300;

    &--row {
      width: 100%;

      @media screen and (min-width: 1024px) {
        width: 50%;
      }

      &.disabled {
        color: @element-disabled;
      }
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    background-color: @status-positive-background;
    justify-content: center;
    align-items: center;
    padding: @size-spacing-x1000 @size-spacing-x450;

    &--container {
      max-width: 1008px;
      width: 100%;
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      gap: @size-spacing-x500;

      @media screen and (min-width: 1024px) {
        flex-direction: row;
        gap: 0;
      }
    }

    &--title {
      color: @status-positive;
      font-weight: 400;
      width: fit-content;

      .base-text {
        color: @element-primary !important;
      }
    }

    &--disclaimer {
      color: @element-primary;
      max-width: 320px;
      width: 100%;
    }
  }

  &__table {
    margin-bottom: @size-spacing-x800;

    &--heading-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: @size-spacing-x1000;
    }

    .helper-view {
      margin: auto;
      padding: @size-spacing-x500 0;
    }

    :deep(.show-divider) {
      &:last-child {
        td {
          border-bottom: none;
        }
      }
    }
  }

  :deep(.data-table__heading) {
    padding: @size-spacing-x350 0;
  }
}

:deep(footer .footer-button__inner p) {
  color: @element-primary !important;
  font-weight: 400;
}

:deep(.navbar-container) {
  background-color: @status-positive-background !important;
}

:deep(.row-item) {
  color: @element-disabled;
  border-top: @size-border-x400 solid @divider-primary;
  border-radius: 0;

  &:first-child {
    border-top: none;
  }

  path {
    fill: @element-disabled;
  }

  @media screen and (min-width: 1024px) {
    &:nth-of-type(2) {
      border-top: none;
    }
  }
}

:deep(.row-item__interaction) {
  color: @text-primary;

  path {
    fill: @element-secondary;
  }
}
</style>
